import React, { useState, useEffect, useRef } from "react"
import { XMarkIcon, Bars3Icon, PencilIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import ContentEditor from "aldoo-ra/CMS/content-editor/index"
import TypeResolver from "aldoo-ra/CMS/type-resolver"
import Modal from "aldoo-ra/Modal"
import { useFieldDragController } from "aldoo-ra/CMS/controllers/field-drag-controller"
import { showNotification } from "aldoo-ra/Notification"
import Typography from "aldoo-ra/Typography"
import _ from "lodash"

const bundleItemOverview = (item) => {
  if (!item?.product) {
    return "No product selected"
  }
  
  // Handle different product data structures:
  // 1. Fully formatted object with name: { name: "Product Name", ... }
  // 2. Post-formatted database reference: { model: "StoreProduct", item: "objectId" }
  // 3. Simple ObjectId string: "685ed44b7a21e906119af9cc"
  let productName = 'No product selected'
  
  if (item.product?.name) {
    // Fully formatted product object
    productName = item.product.name
  } else if (item.product?.model && item.product?.item) {
    // Post-formatted database reference - show loading until pre-formatting resolves it
    productName = 'Loading product...'
  } else if (typeof item.product === 'string') {
    // Raw ObjectId string
    productName = 'Loading product...'
  }
  
  const quantity = item.quantity > 1 ? ` × ${item.quantity}` : ""
  const priceOverride = item.price ? " (Custom Price)" : ""

  return `${productName}${quantity}${priceOverride}`
}

function BundleItemEditor({
  onChange,
  value,
  field,
  parentValues,
  parentObject,
}) {
  const [localValue, setLocalValue] = useState(value ?? [])
  const [formattedItems, setFormattedItems] = useState([])
  const [showDialog, setShowDialog] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [itemContentValues, setItemContentValues] = useState({})
  const isInternalChange = useRef(false)

  // Format bundle items to resolve DatabaseModel references
  useEffect(() => {
    const formatBundleItems = async () => {
      if (!localValue || !Array.isArray(localValue) || localValue.length === 0) {
        setFormattedItems([])
        return
      }
      
      try {
        // Import StoreProduct API to manually resolve product references
        const { default: StoreProductAPI } = await import("aldoo-ra/CMSModels/StoreProduct/api")
        
        const formatted = await Promise.all(
          localValue.map(async (item) => {
            try {
              const formattedItem = { ...item }
              
              // Manually resolve product reference if it's a database reference
              if (item.product && (item.product.model === 'StoreProduct' || typeof item.product === 'string')) {
                const productId = item.product.item || item.product
                
                const productResponse = await StoreProductAPI.Get({ match: { _id: productId } })
                if (productResponse && productResponse.length > 0) {
                  formattedItem.product = productResponse[0]
                }
              }
              
              // Manually resolve price reference if it exists (for future implementation)
              if (item.price && (item.price.model === 'StorePrice' || typeof item.price === 'string')) {
                // Price resolution could be added here if needed
              }
              
              return formattedItem
            } catch (error) {
              console.error("Error formatting bundle item:", error)
              return item // Return original item if formatting fails
            }
          })
        )
        
        setFormattedItems(formatted)
      } catch (error) {
        console.error("Error formatting bundle items:", error)
        setFormattedItems(localValue) // Fallback to original items
      }
    }
    
    formatBundleItems()
  }, [localValue])

  const { getContentType } = TypeResolver()

  // Unified function to update value and notify parent
  const updateValue = (newValue) => {
    isInternalChange.current = true
    setLocalValue(newValue)
    onChange?.(newValue)
  }

  // Drag controller support
  const { handleDragStart, handleDragOver, handleDragEnd } =
    useFieldDragController(localValue, updateValue)

  const isRequired =
    typeof field.required === "function"
      ? field.required(parentObject)
      : field.required

  // Handle external value changes
  useEffect(() => {
    if (!isInternalChange.current && !_.isEqual(value, localValue)) {
      setLocalValue(value ?? [])
    }
    isInternalChange.current = false
  }, [value])

  const handleEdit = (index) => {
    setSelectedIndex(index)
    setItemContentValues(localValue[index] || {})
    setShowDialog(true)
  }

  const handleCloseDialog = () => {
    setShowDialog(false)
    setSelectedIndex(null)
  }

  const handleSaveItem = (newValues) => {
    const updatedValues = [...localValue]

    // Ensure quantity is at least 1
    newValues.quantity = Math.max(1, newValues.quantity || 1)

    if (selectedIndex === -1) {
      // Adding new item
      updatedValues.push(newValues)
    } else {
      // Updating existing item
      updatedValues[selectedIndex] = newValues
    }

    updateValue(updatedValues)
    handleCloseDialog()
  }

  const addNewItem = () => {
    setSelectedIndex(-1)
    setItemContentValues({ quantity: 1 })
    setShowDialog(true)
  }

  const removeItem = (index) => {
    const updatedValues = localValue.filter((_, i) => i !== index)
    updateValue(updatedValues)
  }

  const clearAll = () => {
    updateValue([])
  }

  const renderItem = (item, index) => (
    <div
      key={index}
      className="mt-2 gap-2 flex flex-row items-center w-full"
      draggable
      onDragStart={() => handleDragStart(index)}
      onDragOver={(e) => {
        e.preventDefault()
        handleDragOver(index)
      }}
      onDragEnd={handleDragEnd}
    >
      {localValue.length > 1 && (
        <Bars3Icon className="w-5 h-5 cursor-move text-admin_text_secondary mr-2" />
      )}

      <Typography className="text-admin_text flex flex-row gap-1 items-center">
        {bundleItemOverview(item)}
      </Typography>

      <Button
        variant="admin_icon"
        className="ml-5 text-admin_text"
        onClick={() => handleEdit(index)}
      >
        <PencilIcon className="mx-auto w-6 h-6" />
      </Button>

      <Button
        variant="admin_icon"
        className="text-admin_text hover:text-white hover:bg-admin_error rounded-full p-2 ml-2"
        onClick={() => removeItem(index)}
      >
        <XMarkIcon className="w-6 h-6" />
      </Button>
    </div>
  )

  const validators = (errors) => ({
    product: (value) => {
      if (!value) {
        errors.product = "Product is required"
      }
    },
    quantity: (value) => {
      if (!value || value < 1) {
        errors.quantity = "Quantity must be at least 1"
      }
    },
  })

  return (
    <div className="array-editor">
      <span className="ml-10 text-sm text-admin_text">
        {localValue.length} {localValue.length === 1 ? "item" : "items"}
      </span>

      {localValue?.map((value, index) => {
        // Use formatted item if available, otherwise use raw item
        const formattedItem = formattedItems[index]
        const itemToRender = formattedItem || value
        return renderItem(itemToRender, index)
      })}

      <div className="flex flex-row justify-end items-center mt-4">
        {localValue && (
          <div className="flex items-center gap-5">
            <Button
              variant="admin_secondary"
              className="text-admin_text"
              onClick={addNewItem}
            >
              Add Item
            </Button>

            <Button
              variant="admin_secondary"
              className="text-admin_text"
              onClick={clearAll}
            >
              Clear All
            </Button>
          </div>
        )}
      </div>

      {showDialog && (
        <Modal
          isOpen={showDialog}
          onClose={handleCloseDialog}
          closeOnEscape
          variant="admin_compact"
        >
          <ContentEditor
            title={
              selectedIndex === -1 ? "Add Bundle Item" : "Edit Bundle Item"
            }
            contentType={field.item}
            parentValues={parentValues}
            fieldValues={itemContentValues}
            validators={validators}
            onSave={handleSaveItem}
            onClickSave={handleCloseDialog}
            onError={(errors) => {
              showNotification({
                message: Object.values(errors).find((e) => e),
                className: "bg-admin_error text-white",
              })
            }}
          />
        </Modal>
      )}
    </div>
  )
}

export default BundleItemEditor
